import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { toastConfig } from 'assets/data/config';
import { useSelector } from 'react-redux';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { DGPTAlertRun } from 'types/dgptAlerts';

export const keys = {
  alertRuns: 'alertRuns',
} as const;

interface AlertRunsFilter {
  configId?: string;
  eventInstanceId?: string;
}

interface CreateAlertRunPayload {
  dgptAlertConfigId: string;
  dgptAlertEventInstanceId?: string;
  status: DGPTAlertRun['status'];
  triggerResults?: Record<string, unknown>;
  triggerData?: Record<string, unknown>;
  systemTriggered?: boolean;
}

// Fetch alert runs by config ID or event instance ID, or all runs if no filter provided
export const useAlertRuns = ({
  configId,
  eventInstanceId,
}: AlertRunsFilter = {}) => {
  const groupGuid = useSelector(selectGroupGuid);

  const queryOptions: UseQueryOptions<DGPTAlertRun[], Error> = {
    queryKey: [keys.alertRuns, groupGuid, configId, eventInstanceId],
    queryFn: async () => {
      const params: {
        groupGuid: string;
        configId?: string;
        eventInstanceId?: string;
      } = { groupGuid };

      if (configId) params.configId = configId;
      if (eventInstanceId) params.eventInstanceId = eventInstanceId;

      const response = await axios.get(
        `${config.apiGateway.dgptAlerts}/alert-runs`,
        { params }
      );
      return response.data;
    },
    enabled: true, // Always enabled to fetch all runs when no filter
  };

  return useQuery<DGPTAlertRun[], Error>(queryOptions);
};

// Get an alert run by ID
export const useAlertRunById = (id?: string) => {
  const queryOptions: UseQueryOptions<DGPTAlertRun | null, Error> = {
    queryKey: [keys.alertRuns, 'detail', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await axios.get(
        `${config.apiGateway.dgptAlerts}/alert-runs/${id}`
      );
      return response.data;
    },
    enabled: !!id,
  };

  return useQuery<DGPTAlertRun | null, Error>(queryOptions);
};

// Create an alert run
export const useCreateAlertRun = () => {
  const queryClient = useQueryClient();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);

  return useMutation<DGPTAlertRun, Error, CreateAlertRunPayload>({
    mutationFn: async (data) => {
      const response = await axios.post(
        `${config.apiGateway.dgptAlerts}/alert-runs`,
        {
          ...data,
          groupGuid,
          userGuid,
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      if (variables.dgptAlertConfigId) {
        queryClient.invalidateQueries({
          queryKey: [keys.alertRuns, undefined, variables.dgptAlertConfigId],
        });
      }
      if (variables.dgptAlertEventInstanceId) {
        queryClient.invalidateQueries({
          queryKey: [
            keys.alertRuns,
            undefined,
            undefined,
            variables.dgptAlertEventInstanceId,
          ],
        });
      }
      toast.success('Alert run created successfully.', toastConfig);
    },
    onError: (error) => {
      console.error('Error creating alert run:', error);
      toast.error('Failed to create alert run.', toastConfig);
    },
  });
};

// Check if an alert config has any runs
export const useHasAlertRuns = (configId?: string) => {
  const groupGuid = useSelector(selectGroupGuid);

  const queryOptions: UseQueryOptions<boolean, Error> = {
    queryKey: [keys.alertRuns, 'hasRuns', configId],
    queryFn: async () => {
      if (!configId) return false;

      const params = {
        groupGuid,
        configId,
      };

      const response = await axios.get(
        `${config.apiGateway.dgptAlerts}/alert-runs`,
        { params }
      );

      return response.data && response.data.length > 0;
    },
    enabled: !!configId,
  };

  return useQuery<boolean, Error>(queryOptions);
};
